/**
 * Resolve primary and secondary listed drivers from policy data for multi-vehicle collision flow.
 * @module claims/multiVehiclePolicyDrivers
 */

import type { Driver, PolicyLink } from '../services/types'

/**
 * Returns the policy’s primary driver as a {@link Driver}-shaped record for display and matching.
 * Prefers {@link PolicyLink.mainDriverDetails}; otherwise parses {@link PolicyLink.mainDriver}.
 */
export function policyPrimaryDriverRecord(policy: PolicyLink): Driver | null {
  const details = policy.mainDriverDetails
  if (details && (details.firstName?.trim() || details.lastName?.trim())) {
    return {
      ...details,
      firstName: (details.firstName ?? '').trim(),
      lastName: (details.lastName ?? '').trim(),
    }
  }
  const name = policy.mainDriver?.trim()
  if (!name) return null
  const parts = name.split(/\s+/).filter(Boolean)
  return {
    id: 'primary',
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' ') ?? '',
  }
}

function driverIdentityKey(d: Pick<Driver, 'firstName' | 'lastName'>): string {
  const first = String(d.firstName ?? '')
    .trim()
    .toLowerCase()
  const last = String(d.lastName ?? '')
    .trim()
    .toLowerCase()
  return `${first}|${last}`
}

/**
 * Listed drivers other than the primary (by first+last name match), for “secondary driver” selection.
 */
export function policySecondaryListedDrivers(policy: PolicyLink): Driver[] {
  const primary = policyPrimaryDriverRecord(policy)
  const primaryKey = primary ? driverIdentityKey(primary) : null

  const details = policy.listedDriversDetails
  if (details && details.length > 0) {
    return details.filter((d) => {
      const hasName = Boolean(d.firstName?.trim() || d.lastName?.trim())
      if (!hasName) return false
      if (primaryKey && driverIdentityKey(d) === primaryKey) return false
      return true
    })
  }

  const names = policy.listedDrivers
  if (!names || names.length === 0) return []

  return names
    .map((name, index) => {
      const parts = String(name).split(/\s+/).filter(Boolean)
      return {
        id: String(index + 1),
        firstName: parts[0] ?? '',
        lastName: parts.slice(1).join(' ') ?? '',
      }
    })
    .filter((d) => {
      if (primaryKey && driverIdentityKey(d) === primaryKey) return false
      return Boolean(d.firstName.trim() || d.lastName.trim())
    })
}
